import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import IconContext from '../context/IconContext';

// Define the type of the React component being given via context
type SvgComponentProps = React.SVGProps<SVGSVGElement> 
// Create an interface containing the icons needed for this component
interface IIconContext {
    IconCopy: React.ComponentType<SvgComponentProps>;
    IconCheckmark: React.ComponentType<SvgComponentProps>;
    // To use custom SVGs, update the config to define these keys.
    IconParagraphCopy: React.ComponentType<SvgComponentProps>;
    IconParagraphCheckmark: React.ComponentType<SvgComponentProps>;
}

interface ParagraphProps extends React.HTMLAttributes<HTMLParagraphElement> {
    ellipsis?: boolean;
    expandable?: boolean;
    symbol?: string;
    copyable?: boolean;
    text?: string;
    format?: string;
    tooltip?: string;
    editable?: boolean;
    editing?: boolean;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Functions
    onCopy?: () => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCancel?: () => void;
    onConfirm?: () => void;
}

/**
 * An area of text that can have additional `cai-fusion` included properties.
 * 
 * ## _
 * ### Arguments
 * @param {boolean} ellipsis - A flag to add ellipses when text extends past its bounds. [UNUSED]
 * @param {boolean} expandable - A flag to allow the text area to be expanded on click of a button. [UNUSED]
 * @param {string} symbol - Custom description of ellipsis. [UNUSED]
 * @param {boolean} copyable - A flag to allow the text to be copied to the clipboard.
 * @param {string} text - The text used to populate the paragraph. [UNUSED]
 * @param {string} format - [UNUSED]
 * @param {string} tooltip - Text to appear on hover. [UNUSED]
 * @param {boolean} editable - A flag to allow the text to be edited.
 * @param {boolean} editing - [UNUSED]
 * @param {React.ReactNode} children - The contents inside the paragraph.
 * @param {string} className - CSS classes to apply to the paragraph.
 * 
 * ## _
 * ### Functions
 * @param {function} onCopy - Function that is called when text is copied.
 * @param {function} onChange - Function that is called when text field is updated when editing. [UNUSED]
 * @param {function} onCancel - Funcion that is called when edit field is canceled. [UNUSED]
 * @param {function} onConfirm - Function that is called when text is updated after editing. [UNUSED]
 * 
 * ## _
 * ### Icons
 * Declare these in your application's icon configuration.
 * @inner `IconParagraphCopy` - An optional icon override for the copy button.
 * @inner `IconParagraphCheckmark` - An optional icon override for the checkmark feedback icon.
 */
class Paragraph extends React.Component<ParagraphProps> {
    // Establish the context and tell TypeScript that we're TOTALLY having it in the shape of this thing.
    static contextType = IconContext;
    context!: IIconContext;

    static propTypes = {
        ellipsis: PropTypes.bool,
        expandable: PropTypes.bool,
        symbol: PropTypes.string,
        copyable: PropTypes.bool,
        text: PropTypes.string,
        format: PropTypes.string,
        tooltip: PropTypes.string,
        editable: PropTypes.bool,
        editing: PropTypes.bool,
        children: PropTypes.node,
        className: PropTypes.string,
        onCopy: PropTypes.func,
        onChange: PropTypes.func,
        onCancel: PropTypes.func,
        onConfirm: PropTypes.func,
    }

    static defaultProps = {
        className: "",
        ellipsis: false,
        expandable: false,
        copyable: false,
        editable: false,
    }

    state = {
        copyState: true
    }

    copyText = () => {
        console.log("copying");
        const copytext = React.Children.toArray(this.props.children).join("")
        navigator.clipboard.writeText(String(copytext));
        this.setState({copyState: false});
        setTimeout(() => this.setState({copyState: true}), 3000);
    }

    render() {
        const {
            ellipsis,
            expandable,
            symbol,
            copyable,
            text,
            format,
            tooltip,
            editable,
            editing,
            children,
            className,
            onCopy,
            onChange,
            onCancel,
            onConfirm,
            ...rest
        } = this.props;

        const { IconCopy, IconCheckmark, IconParagraphCopy, IconParagraphCheckmark } = this.context;
        // If a specific SVG is defined, choose that, else default to generic icon.
        const CopyIcon = IconParagraphCopy || IconCopy;
        const CheckIcon = IconParagraphCheckmark || IconCheckmark;

        let classes = className + 'p';
        if (ellipsis) classes += ' ellipsis';
        if (expandable) classes += ' expandable';
        if (copyable) classes += ' copyable';
        if (editable) classes += ' editable';
        if (className) classes += ` ${className}`;

        // TODO: Handle ellipsis, editable functionality

        return (
            <>
                <p className={classes} {...rest}>
                    {children}
                    { copyable && 
                            (this.state.copyState ?
                            <CopyIcon className="m-chat-event__icon" onClick={this.copyText}/> :
                            <CheckIcon className="m-chat-event__icon"/>)
                    }
                </p>
            </>
        );
    }
}

export default Paragraph;
